/**
 * useGoSport - Hook principal de gestion du module Go Sport
 * Gère le profil, les sessions, le wizard, la persistance localStorage + Firestore
 * Quick-start : si le profil est complet, accès direct à la séance
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { calculateLevel } from '../data/levels';
import { generateSession } from '../data/exercises';
import { HIIT_RATIOS } from '../data/levels';
import { auth } from '../../../services/firebase';
import {
  saveGoSportData,
  subscribeToGoSportData,
  isAuthenticated
} from '../../../services/firestoreSync';

const STORAGE_KEY = 'adhd-planner-go-sport';
const NOTIF_STORAGE_KEY = 'adhd-planner-go-sport-notif';
const INTERRUPTED_SESSION_KEY = 'adhd-planner-go-sport-interrupted';

// Request notification permission and schedule reminders
function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function scheduleSessionReminder(lastSessionDate, streakCurrent) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  // Clear previous reminder
  const prevTimer = localStorage.getItem(NOTIF_STORAGE_KEY);
  if (prevTimer) clearTimeout(Number(prevTimer));

  // Reminder after 48h from last session
  const reminderTime = lastSessionDate
    ? new Date(lastSessionDate).getTime() + (48 * 60 * 60 * 1000) - Date.now()
    : 0;

  if (reminderTime <= 0) return;

  const timerId = setTimeout(() => {
    const messages = [
      `Ta streak est à ${streakCurrent} ! Ne la perds pas 💪`,
      'Ça fait 48h — prêt(e) pour une séance Go Sport ? 🏋️',
      'Ton corps est reposé, lance une séance HIIT ! 🔥',
    ];
    const msg = messages[Math.floor(Math.random() * messages.length)];

    try {
      new Notification('Go Sport — ADHDManager', {
        body: msg,
        icon: '/favicon.ico',
        tag: 'gosport-reminder',
      });
    } catch { /* ignore notification errors */ }

    localStorage.removeItem(NOTIF_STORAGE_KEY);
  }, Math.min(reminderTime, 2147483647)); // Cap to max setTimeout

  localStorage.setItem(NOTIF_STORAGE_KEY, String(timerId));
}

const DEFAULT_STATE = {
  userLevel: null,
  profile: {
    objectives: [],
    limitations: [],
    hasMedication: false,
    medicationType: null,
    hasHypertension: false,
    disclaimerAccepted: false,
    healthAnswers: {},
    evaluationAnswers: {},
    preferredDuration: 30,
    preferredZone: 'full',
  },
  sessions: [],
  streak: {
    current: 0,
    best: 0,
    lastSession: null,
  },
};

export function useGoSport() {
  const [data, setData] = useLocalStorage(STORAGE_KEY, DEFAULT_STATE);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionExercises, setSessionExercises] = useState([]);
  const unsubscribeRef = useRef(null);
  const syncTimeoutRef = useRef(null);

  // ========== Session Interruption Resume ==========

  const [interruptedSession, setInterruptedSession] = useState(() => {
    try {
      const saved = localStorage.getItem(INTERRUPTED_SESSION_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const elapsed = Date.now() - parsed.interruptedAt;
      const MAX_RESUME_MS = 30 * 60 * 1000; // 30 minutes
      if (elapsed > MAX_RESUME_MS) {
        localStorage.removeItem(INTERRUPTED_SESSION_KEY);
        return null; // Too old — discard
      }
      return parsed;
    } catch { return null; }
  });

  // Save session state when app might close during an active session
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentSession?.status === 'active') {
        try {
          localStorage.setItem(INTERRUPTED_SESSION_KEY, JSON.stringify({
            ...currentSession,
            exercises: sessionExercises,
            interruptedAt: Date.now(),
          }));
        } catch { /* ignore */ }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentSession, sessionExercises]);

  const resumeInterruptedSession = useCallback(() => {
    if (!interruptedSession) return;
    setCurrentSession({
      ...interruptedSession,
      status: 'active',
    });
    setSessionExercises(interruptedSession.exercises || []);
    setInterruptedSession(null);
    localStorage.removeItem(INTERRUPTED_SESSION_KEY);
    setWizardStep(5); // Go to live session step
    setShowWizard(true);
  }, [interruptedSession]);

  const discardInterruptedSession = useCallback(() => {
    setInterruptedSession(null);
    localStorage.removeItem(INTERRUPTED_SESSION_KEY);
  }, []);

  // ========== Notifications ==========

  // Request permission on mount & schedule reminder if profile exists
  useEffect(() => {
    if (data.userLevel !== null) {
      requestNotificationPermission();
      if (data.streak.lastSession) {
        scheduleSessionReminder(data.streak.lastSession, data.streak.current);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========== Firestore Sync ==========

  // Debounced save to Firestore
  const syncToFirestore = useCallback((newData) => {
    if (!isAuthenticated()) return;

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      saveGoSportData(newData).catch(err => {
        console.error('Go Sport Firestore sync error:', err);
      });
    }, 1000);
  }, []);

  // Subscribe to Firestore on auth
  useEffect(() => {
    const setupSubscription = () => {
      if (!isAuthenticated()) return;

      try {
        unsubscribeRef.current = subscribeToGoSportData(
          (firestoreData) => {
            if (firestoreData && firestoreData.userLevel !== undefined) {
              // Merge: Firestore wins for profile, merge sessions
              setData(prev => {
                const merged = {
                  ...prev,
                  userLevel: firestoreData.userLevel ?? prev.userLevel,
                  profile: { ...prev.profile, ...firestoreData.profile },
                  streak: firestoreData.streak || prev.streak,
                  sessions: firestoreData.sessions || prev.sessions,
                };
                return merged;
              });
            }
          },
          (error) => {
            console.error('Go Sport subscription error:', error);
          }
        );
      } catch (error) {
        console.error('Go Sport setup subscription error:', error);
      }
    };

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setupSubscription();
      } else {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      }
    });

    setupSubscription();

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) unsubscribeRef.current();
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: update data + sync
  const updateData = useCallback((updater) => {
    setData(prev => {
      const newData = typeof updater === 'function' ? updater(prev) : updater;
      syncToFirestore(newData);
      return newData;
    });
  }, [setData, syncToFirestore]);

  // ========== Profile ==========

  const hasProfile = data.userLevel !== null;

  // Check if profile is complete enough for quick-start
  const isProfileComplete = useMemo(() => {
    return (
      data.userLevel !== null &&
      data.profile.disclaimerAccepted &&
      Object.keys(data.profile.evaluationAnswers || {}).length > 0
    );
  }, [data.userLevel, data.profile.disclaimerAccepted, data.profile.evaluationAnswers]);

  const updateProfile = useCallback((updates) => {
    updateData(prev => ({
      ...prev,
      profile: { ...prev.profile, ...updates },
    }));
  }, [updateData]);

  const setUserLevel = useCallback((level) => {
    updateData(prev => ({ ...prev, userLevel: level }));
  }, [updateData]);

  const saveEvaluationAnswers = useCallback((answers) => {
    const level = calculateLevel(answers);
    updateData(prev => ({
      ...prev,
      userLevel: level,
      profile: { ...prev.profile, evaluationAnswers: answers },
    }));
    return level;
  }, [updateData]);

  // ========== Session Generation ==========

  const generateNewSession = useCallback((overrides = {}) => {
    const level = overrides.level || data.userLevel || 1;
    const zone = overrides.zone || data.profile.preferredZone || 'full';
    const duration = overrides.duration || data.profile.preferredDuration || 30;

    const ratio = HIIT_RATIOS.find(r => r.level === level) || HIIT_RATIOS[0];
    const cycleTime = ratio.work + ratio.rest;
    const totalCycles = Math.min(
      ratio.cycles,
      Math.floor((duration * 60) / cycleTime)
    );

    const exercises = generateSession(level, zone, totalCycles);

    const session = {
      id: `session_${Date.now()}`,
      date: new Date().toISOString(),
      level,
      zone,
      duration,
      work: ratio.work,
      rest: ratio.rest,
      totalCycles,
      exercises: exercises.map((ex, i) => ({
        ...ex,
        cycleIndex: i,
      })),
      status: 'ready',
      completedExercises: 0,
      startTime: null,
      endTime: null,
      caloriesEstimated: 0,
      rating: null,
    };

    setCurrentSession(session);
    setSessionExercises(session.exercises);
    return session;
  }, [data.userLevel, data.profile.preferredZone, data.profile.preferredDuration]);

  // ========== Session Lifecycle ==========

  const startSession = useCallback(() => {
    if (!currentSession) return;
    setCurrentSession(prev => ({
      ...prev,
      status: 'active',
      startTime: Date.now(),
    }));
  }, [currentSession]);

  // Auto level progression: check if user should level up or down
  const checkLevelProgression = useCallback((sessions, currentLevel) => {
    // Need at least 3 sessions at current level to evaluate
    const recentAtLevel = sessions
      .filter(s => s.level === currentLevel)
      .slice(-5); // Last 5 sessions at this level

    if (recentAtLevel.length < 3) return currentLevel;

    const avgRating = recentAtLevel.reduce((s, r) => s + (r.rating || 3), 0) / recentAtLevel.length;
    const completionRate = recentAtLevel.filter(s =>
      s.exercisesCompleted >= (s.totalCycles || s.exercisesCompleted)
    ).length / recentAtLevel.length;

    // Level up: avg rating >= 4 and completion rate >= 80%
    if (avgRating >= 4 && completionRate >= 0.8 && currentLevel < 10) {
      return currentLevel + 1;
    }

    // Level down: avg rating <= 2 and more than 3 sessions
    if (avgRating <= 2 && recentAtLevel.length >= 3 && currentLevel > 1) {
      return currentLevel - 1;
    }

    return currentLevel;
  }, []);

  const completeSession = useCallback((sessionData) => {
    const endTime = Date.now();
    const duration = currentSession?.startTime
      ? Math.round((endTime - currentSession.startTime) / 1000)
      : 0;

    const caloriesEstimated = Math.round(
      (duration / 60) * (5 + (data.userLevel || 1) * 0.8)
    );

    const completedSession = {
      ...currentSession,
      ...sessionData,
      status: 'completed',
      endTime,
      duration,
      caloriesEstimated,
    };

    // Update streak
    const today = new Date().toISOString().split('T')[0];
    const lastSession = data.streak.lastSession;
    let newStreak = data.streak.current;

    if (lastSession) {
      const lastDate = new Date(lastSession);
      const todayDate = new Date(today);
      const diffDays = Math.round((todayDate - lastDate) / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        newStreak += 1;
      } else if (diffDays > 1) {
        newStreak = 1;
      }
    } else {
      newStreak = 1;
    }

    const newSession = {
      id: completedSession.id,
      date: completedSession.date,
      level: completedSession.level,
      duration: completedSession.duration,
      exercisesCompleted: completedSession.completedCycles || completedSession.totalCycles,
      totalCycles: completedSession.totalCycles,
      caloriesEstimated,
      rating: completedSession.rating,
    };

    const allSessions = [...data.sessions, newSession];

    // Check level progression
    const newLevel = checkLevelProgression(allSessions, data.userLevel || 1);

    updateData(prev => ({
      ...prev,
      userLevel: newLevel,
      sessions: allSessions,
      streak: {
        current: newStreak,
        best: Math.max(data.streak.best, newStreak),
        lastSession: today,
      },
    }));

    // Schedule next session reminder
    scheduleSessionReminder(today, newStreak);

    setCurrentSession(null);
    completedSession.newLevel = newLevel;
    completedSession.leveledUp = newLevel > (data.userLevel || 1);
    completedSession.leveledDown = newLevel < (data.userLevel || 1);
    return completedSession;
  }, [currentSession, data.userLevel, data.sessions, data.streak, updateData, checkLevelProgression]);

  const abandonSession = useCallback(() => {
    setCurrentSession(null);
    setSessionExercises([]);
    localStorage.removeItem(INTERRUPTED_SESSION_KEY);
  }, []);

  const replaceExercise = useCallback((index, newExercise) => {
    setSessionExercises(prev => {
      const updated = [...prev];
      updated[index] = { ...newExercise, cycleIndex: index };
      return updated;
    });
  }, []);

  // ========== Wizard Navigation ==========

  const openWizard = useCallback((startStep = 0) => {
    // Quick-start: if profile is complete, go directly to session setup
    if (isProfileComplete && startStep === 0) {
      setWizardStep(0); // Show welcome with quick-start button
    } else {
      setWizardStep(startStep);
    }
    setShowWizard(true);
  }, [isProfileComplete]);

  const quickStart = useCallback(() => {
    if (isProfileComplete) {
      generateNewSession();
      setWizardStep(4); // Go directly to session setup
      setShowWizard(true);
    } else if (hasProfile) {
      // Profile exists but incomplete (e.g. disclaimer not accepted)
      generateNewSession();
      setWizardStep(4);
      setShowWizard(true);
    } else {
      openWizard(0);
    }
  }, [isProfileComplete, hasProfile, generateNewSession, openWizard]);

  const closeWizard = useCallback(() => {
    setShowWizard(false);
    setWizardStep(0);
  }, []);

  const nextStep = useCallback(() => {
    setWizardStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setWizardStep(prev => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setWizardStep(step);
  }, []);

  // ========== Stats ==========

  const stats = useMemo(() => {
    const sessions = data.sessions || [];
    const totalSessions = sessions.length;
    const totalDuration = sessions.reduce((s, sess) => s + (sess.duration || 0), 0);
    const totalCalories = sessions.reduce((s, sess) => s + (sess.caloriesEstimated || 0), 0);
    const avgRating = totalSessions > 0
      ? sessions.reduce((s, sess) => s + (sess.rating || 0), 0) / totalSessions
      : 0;

    return {
      totalSessions,
      totalDuration,
      totalCalories,
      avgRating: Math.round(avgRating * 10) / 10,
      streak: data.streak,
      level: data.userLevel,
    };
  }, [data.sessions, data.streak, data.userLevel]);

  return {
    // State
    data,
    hasProfile,
    isProfileComplete,
    showWizard,
    wizardStep,
    currentSession,
    sessionExercises,
    stats,

    // Profile
    updateProfile,
    setUserLevel,
    saveEvaluationAnswers,

    // Session
    generateNewSession,
    startSession,
    completeSession,
    abandonSession,
    replaceExercise,
    setCurrentSession,

    // Wizard
    openWizard,
    quickStart,
    closeWizard,
    nextStep,
    prevStep,
    goToStep,
    setShowWizard,
    setWizardStep,

    // Interruption
    interruptedSession,
    resumeInterruptedSession,
    discardInterruptedSession,
  };
}
